import { Button, Drawer, Flex, Form, Input } from 'antd';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import { general, generalFields } from 'shared/model/schemas/rules';
import { useMessage } from 'shared/libs/hooks';
import { useChange3dsMutation } from '../api';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrentEmi } from 'shared/config/store/selectors/app';
import { Account } from 'entities/account/model/types';
import { AppDispatch, RootState } from 'shared/config/store/store';
import { getEMICurrentAccountObject } from 'shared/config/store/reducers/accountsSlice';
import { Card } from 'entities/card/model/types';
import { getAccountCards, updateCard } from 'shared/config/store/reducers/cardsSlice';
import { setActivity } from 'shared/config/store/reducers/appSlice';

type Props = {
  open: boolean;
  onClose: () => unknown;
}

export const Reset3dsDrawer: FC<Props> = ({ open, onClose }) => {
  const { formatMessage: t } = useIntl();
  const { showSuccess, showError } = useMessage();
  const dispatch = useDispatch<AppDispatch>();
  const [form] = Form.useForm();
  const formRules = {
    email: [general(t)['required-field'], ...generalFields(t).email],
    '3ds': [general(t)['required-field'], ...generalFields(t)['3ds']],
    phone: [general(t)['required-field']],
  };

  const currentEmi = useSelector(getCurrentEmi);
  const currentAccount: Account = useSelector((state: RootState) =>
    getEMICurrentAccountObject(state, currentEmi)
  ) as Account;
  const card: Card = useSelector(
    getAccountCards(currentEmi, currentAccount.id)
  )?.[0] as Card;

  const cardId = card.id;
  const card3ds = card['3d_secure_settings'];

  const [change3ds] = useChange3dsMutation();

  const onSubmit = (v: {
    email: string;
    phone: string;
    '3ds': string;
  }) => {
    dispatch(setActivity(true));
    change3ds({
      accountId: String(currentAccount.id),
      cardId: String(cardId),
      email: v.email,
      password: v['3ds'],
      phone: v.phone
    })
      .unwrap()
      .then(async (res) => {
        dispatch(updateCard({
          emi: currentEmi,
          accountId: currentAccount.id,
          cardId,
          newCard: res.response,
        }));
        showSuccess?.(t({ id: 'card.3ds.settingsUpdated' }));
        onClose();
      })
      .catch((err) => {
        showError?.(t({ id: 'card.3ds.settingsError' }));
      })
      .finally(() => {
        dispatch(setActivity(false));
      })
  }

  return (
    <Drawer
      title={t({ id: 'card.3ds.reset' })}
      open={open}
      closable
      onClose={onClose}
      width={560}
    >
      <Flex vertical align='center' gap={16}>
        <Form
          form={form}
          onFinish={(e) => onSubmit(e)}
          className='w100'
          initialValues={{
            email: card3ds.email,
            phone: card3ds.mobile,
            '3ds': ''
          }}
        >
          <Flex vertical align='center'>
            <Form.Item className='w100' name="email" rules={formRules.email}>
              <Input placeholder={t({ id: 'inputs.email' })} />
            </Form.Item>
            <Form.Item className='w100' name="3ds" rules={formRules['3ds']}>
              <Input.Password placeholder={t({ id: 'fields.3ds.new' })} />
            </Form.Item>
            <Form.Item className='w100' name="phone" rules={formRules.phone}>
              <Input placeholder={t({ id: 'screens.setting.setting.setting.nav.phone' })} />
            </Form.Item>
            <Button
              id="btn__account-name-submit"
              variant="solid"
              color='default'
              onClick={form.submit}>
              {t({ id: 'buttons.change' })}
            </Button>
          </Flex>
        </Form>
      </Flex>
    </Drawer>
  )
}